import {
    MediaImageGallery,
    MediaModalPortal,
    PhotoCount,
    useJobStory,
} from 'rwl-ui';
import { CTABar } from '../CTABar';
import { CloseButton } from '../../CloseButton';
import styled from '@emotion/styled';
import { WhiteCloseSVG } from '../../themes/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useModalStack } from '../../hooks/useModalStack';
import { pluginMediaFromJob } from 'rwl-ui';
import { useFetchJobDetails } from '../../fetchers/JobFetcher/useFetchJobDetails';
import { useEnqueueEngagement, useGlobal } from '../../hooks';
import { ACTION_TYPE } from 'rwl-shared/dist/models/EngagementEvent';

const ModalRefContainer = styled.div`
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
`;

const ModalContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    background-color: black;
    left: 50%;
    transform: translateX(-50%);
    top: 0;
    bottom: 0;
    width: 100%;
    max-width: 900px;
`;

const HeaderContainer = styled.header`
    height: var(--rwl-header-height);
    display: flex;
    background-color: black;
    width: 100%;
    color: white;
`;

const FooterContainer = styled.div`
    height: var(--rwl-header-height);
    display: flex;
    background-color: var(--rwl-background-primary);
    justify-content: space-between;
    align-items: stretch;
    border-top: 1px solid var(--rwl-color-neutral-border);
    width: 100%;
    container-type: inline-size;
    container-name: media-footer;
`;

export const MediaModalContainer = (): JSX.Element | null => {
    const { jobStoryId, selectedMedia, setSelectedMedia } = useJobStory();
    const { accountId } = useGlobal();
    const { data: details } = useFetchJobDetails({
        jobId: jobStoryId,
        accountId,
    });
    const containerRef = useRef<HTMLDivElement>(null);

    const { registerModal, unregisterModal } = useModalStack();
    const [mediaIndex, setMediaIndex] = useState<number>(0);

    const { enqueue } = useEnqueueEngagement();

    const handleOnClose = () => {
        setSelectedMedia(undefined);
    };

    const allMedia = details?.job ? pluginMediaFromJob(details.job) : [];

    useEffect(() => {
        if (selectedMedia) {
            registerModal('media-carousel-modal', handleOnClose);
            enqueue({
                actionType: ACTION_TYPE.VIEW,
                type: 'Engagement',
                target: 'Media Carousel',
                mediaId: selectedMedia.id,
            });
        } else {
            unregisterModal('media-carousel-modal');
        }
    }, [selectedMedia]);

    const caption = useMemo(() => {
        const allCheckins = details?.job?.content?.filter((content) => {
            return content.type === 'Checkin';
        });
        const selectedCheckin = allCheckins?.find((checkin) => {
            return checkin.media.some((media) => {
                return media.id === selectedMedia?.id;
            });
        });
        return selectedCheckin?.message;
    }, [selectedMedia, details?.job?.content]);

    return (
        <ModalRefContainer ref={containerRef}>
            <MediaModalPortal
                onClose={handleOnClose}
                isOpen={!!selectedMedia}
                container={containerRef.current}
            >
                <ModalContainer role="dialog">
                    {selectedMedia && (
                        <>
                            <HeaderContainer>
                                <div
                                    style={{
                                        flex: 1,
                                        position: 'absolute',
                                        width: '100%',
                                    }}
                                >
                                    <PhotoCount
                                        mediaCount={allMedia?.length}
                                        mediaIndex={mediaIndex}
                                    />
                                </div>
                                <div style={{ position: 'absolute', right: 0 }}>
                                    <CloseButton onClick={handleOnClose}>
                                        <WhiteCloseSVG />
                                    </CloseButton>
                                </div>
                            </HeaderContainer>
                            <MediaImageGallery
                                onChange={(media: any, index: number) => {
                                    setSelectedMedia(
                                        allMedia.find((m) => m.id === media.id)
                                    );
                                    setMediaIndex(index);
                                }}
                                media={selectedMedia}
                                setMediaIndex={setMediaIndex}
                                allMedia={allMedia}
                                mediaIndex={mediaIndex}
                                caption={caption}
                            />
                            <FooterContainer>
                                <div />
                                <CTABar mediaId={selectedMedia.id} />
                            </FooterContainer>
                        </>
                    )}
                </ModalContainer>
            </MediaModalPortal>
        </ModalRefContainer>
    );
};
